import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useActivity } from '../App';

const api = () => axios.create({
  baseURL: 'http://localhost:8080',
  headers: { Authorization: `Bearer ${localStorage.getItem('wb_admin_token')}` },
});

const STATUS_CFG = {
  PENDING:    { color: '#f59e0b', bg: 'rgba(245,158,11,0.12)',   label: 'Pending'    },
  PROCESSING: { color: '#06b6d4', bg: 'rgba(6,182,212,0.12)',    label: 'Processing' },
  COMPLETED:  { color: '#22c55e', bg: 'rgba(34,197,94,0.12)',    label: 'Completed'  },
  REJECTED:   { color: '#ef4444', bg: 'rgba(239,68,68,0.12)',    label: 'Rejected'   },
};

const downloadCSV = (data) => {
  const headers = ['ID', 'User', 'User ID', 'Amount (Coins)', 'Method', 'Payment Handle', 'Status', 'Admin Note', 'Requested At', 'Processed At'];
  const rows = data.map(r => [
    r.id, r.userName || '', r.userId, r.amount, r.method, r.paymentHandle, r.status,
    r.adminNote || '', r.requestedAt ? new Date(r.requestedAt).toLocaleString('en-IN') : '',
    r.processedAt ? new Date(r.processedAt).toLocaleString('en-IN') : '',
  ]);
  const csv = [headers, ...rows].map(r => r.map(v => `"${v ?? ''}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = 'withdrawals.csv'; a.click();
  URL.revokeObjectURL(url);
};

const Withdrawals = () => {
  const { addLog } = useActivity();
  const [data, setData]           = useState([]);
  const [allData, setAllData]     = useState([]);
  const [total, setTotal]         = useState(0);
  const [loading, setLoading]     = useState(true);
  const [page, setPage]           = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [modal, setModal]         = useState(null); // { row, action }
  const [adminNote, setAdminNote] = useState('');
  const [updating, setUpdating]   = useState(false);
  const SIZE = 20;

  const loadAll = useCallback(async () => {
    try {
      const res = await api().get('/api/admin/withdrawals', { params: { page: 0, size: 9999 } });
      setAllData(res.data?.data || []);
    } catch { /* silent */ }
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, size: SIZE };
      if (statusFilter) params.status = statusFilter;
      const res = await api().get('/api/admin/withdrawals', { params });
      setData(res.data?.data || []);
      setTotal(res.data?.total || 0);
    } catch { toast.error('Failed to load withdrawals'); }
    finally { setLoading(false); }
  }, [page, statusFilter]);

  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => { loadData(); }, [loadData]);

  const updateStatus = async (id, status, note) => {
    setUpdating(true);
    try {
      await api().put(`/api/admin/withdrawals/${id}/status`, { status, adminNote: note || '' });
      toast.success(`Withdrawal ${status.toLowerCase()} successfully`);
      addLog && addLog(`Withdrawal #${id} → ${status}`);
      setModal(null);
      setAdminNote('');
      loadData();
      loadAll();
    } catch { toast.error('Failed to update withdrawal status'); }
    finally { setUpdating(false); }
  };

  const openModal = (row, action) => { setModal({ row, action }); setAdminNote(''); };

  const totalPages = Math.ceil(total / SIZE);
  const pendingCount    = allData.filter(d => d.status === 'PENDING').length;
  const processingCount = allData.filter(d => d.status === 'PROCESSING').length;
  const completedCount  = allData.filter(d => d.status === 'COMPLETED').length;
  const rejectedCount   = allData.filter(d => d.status === 'REJECTED').length;
  const totalCoins      = allData.filter(d => d.status === 'COMPLETED').reduce((s, d) => s + (d.amount || 0), 0);

  const STATUS_TABS = [
    { key: '', label: 'All', count: allData.length },
    { key: 'PENDING', label: 'Pending', count: pendingCount },
    { key: 'PROCESSING', label: 'Processing', count: processingCount },
    { key: 'COMPLETED', label: 'Completed', count: completedCount },
    { key: 'REJECTED', label: 'Rejected', count: rejectedCount },
  ];

  return (
    <div className="animate-in">
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '24px' }}>
        <div>
          <div style={{ fontSize: '0.76rem', color: 'var(--text-muted)', marginBottom: '4px' }}>Finance / <span style={{ color: 'var(--text-secondary)' }}>Withdrawals</span></div>
          <h1 style={{ margin: 0, fontSize: '1.6rem', fontWeight: 800 }}>💸 Withdrawals</h1>
          <p style={{ margin: '4px 0 0', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>Manage user withdrawal requests and process payouts</p>
        </div>
        <button style={exportBtnS} onClick={() => downloadCSV(allData)}>📥 Export CSV</button>
      </div>

      {/* Stat Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '14px', marginBottom: '24px' }}>
        {[
          { label: 'Total Requests',       value: allData.length,                     color: 'var(--accent-primary)', icon: '📋' },
          { label: 'Pending',              value: pendingCount,                        color: '#f59e0b',               icon: '⏳' },
          { label: 'Completed',            value: completedCount,                      color: '#22c55e',               icon: '✅' },
          { label: 'Total Coins Withdrawn',value: totalCoins.toLocaleString() + ' 🪙', color: '#6366f1',               icon: '💰' },
        ].map((s, i) => (
          <div key={i} style={cardS}>
            <div style={{ fontSize: '1.4rem' }}>{s.icon}</div>
            <div style={{ fontSize: '1.4rem', fontWeight: 800, color: s.color, marginTop: '4px' }}>{s.value}</div>
            <div style={{ fontSize: '0.76rem', color: 'var(--text-muted)' }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Status Filter Tabs */}
      <div style={{ display: 'flex', gap: '6px', marginBottom: '16px', flexWrap: 'wrap' }}>
        {STATUS_TABS.map(tab => (
          <button
            key={tab.key}
            onClick={() => { setStatusFilter(tab.key); setPage(0); }}
            style={{
              padding: '7px 14px', borderRadius: '8px', border: '1px solid',
              cursor: 'pointer', fontSize: '0.84rem', fontWeight: 600, transition: 'all 0.15s',
              borderColor: statusFilter === tab.key ? 'var(--accent-primary)' : 'var(--border-color)',
              background: statusFilter === tab.key ? 'rgba(99,102,241,0.15)' : 'var(--bg-tertiary)',
              color: statusFilter === tab.key ? 'var(--accent-primary)' : 'var(--text-secondary)',
            }}
          >
            {tab.label}
            <span style={{ marginLeft: '6px', fontSize: '0.72rem', padding: '1px 6px', borderRadius: '10px',
              background: statusFilter === tab.key ? 'rgba(99,102,241,0.2)' : 'var(--bg-hover)',
              color: statusFilter === tab.key ? 'var(--accent-primary)' : 'var(--text-muted)' }}>
              {tab.count}
            </span>
          </button>
        ))}
      </div>

      {/* Table */}
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: '12px', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--bg-tertiary)', borderBottom: '1px solid var(--border-color)' }}>
                {['#', 'User', 'Amount', 'Method', 'Payment Handle', 'Status', 'Requested At', 'Actions'].map(h => (
                  <th key={h} style={thS}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: '50px', color: 'var(--text-muted)' }}>Loading…</td></tr>
              ) : data.length === 0 ? (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: '50px', color: 'var(--text-muted)' }}>No withdrawals found</td></tr>
              ) : data.map((row, i) => {
                const cfg = STATUS_CFG[row.status] || STATUS_CFG.PENDING;
                return (
                  <tr key={row.id}
                    style={{ borderBottom: '1px solid var(--border-color)', transition: 'background 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <td style={tdS}>{page * SIZE + i + 1}</td>
                    <td style={tdS}>
                      <div style={{ fontSize: '0.84rem', fontWeight: 600 }}>{row.userName || '—'}</div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>ID: {row.userId}</div>
                    </td>
                    <td style={tdS}>
                      <span style={{ fontWeight: 800, fontSize: '0.95rem', color: '#f59e0b' }}>
                        {row.amount?.toLocaleString()} 🪙
                      </span>
                    </td>
                    <td style={tdS}>
                      <span style={{ fontSize: '0.76rem', fontWeight: 700, padding: '3px 8px', borderRadius: '6px',
                        background: row.method === 'UPI' ? 'rgba(99,102,241,0.12)' : 'rgba(6,182,212,0.12)',
                        color: row.method === 'UPI' ? '#818cf8' : '#06b6d4' }}>
                        {row.method || '—'}
                      </span>
                    </td>
                    <td style={{ ...tdS, fontFamily: 'monospace', fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
                      {row.paymentHandle || '—'}
                    </td>
                    <td style={tdS}>
                      <span style={{ fontSize: '0.76rem', fontWeight: 700, padding: '3px 10px', borderRadius: '20px',
                        background: cfg.bg, color: cfg.color }}>
                        {cfg.label}
                      </span>
                    </td>
                    <td style={{ ...tdS, fontSize: '0.78rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                      {row.requestedAt ? new Date(row.requestedAt).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}
                    </td>
                    <td style={{ ...tdS, whiteSpace: 'nowrap' }}>
                      <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                        {row.status === 'PENDING' && (
                          <button style={processBtnS} onClick={() => updateStatus(row.id, 'PROCESSING', '')}>⚡ Process</button>
                        )}
                        {(row.status === 'PENDING' || row.status === 'PROCESSING') && (<>
                          <button style={approveBtnS} onClick={() => openModal(row, 'COMPLETED')}>✅ Approve</button>
                          <button style={rejectBtnS}  onClick={() => openModal(row, 'REJECTED')}>❌ Reject</button>
                        </>)}
                        {(row.status === 'COMPLETED' || row.status === 'REJECTED') && (
                          <span style={{ fontSize: '0.76rem', color: 'var(--text-muted)', padding: '4px 0' }}>—</span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderTop: '1px solid var(--border-color)' }}>
            <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
              {page * SIZE + 1}–{Math.min((page + 1) * SIZE, total)} of {total}
            </span>
            <div style={{ display: 'flex', gap: '6px' }}>
              <button disabled={page === 0} onClick={() => setPage(p => p - 1)}
                style={{ ...paginBtnS, opacity: page === 0 ? 0.4 : 1 }}>‹</button>
              <span style={{ padding: '5px 12px', background: 'var(--accent-primary)', color: 'white', borderRadius: '6px', fontSize: '0.82rem', fontWeight: 600 }}>
                {page + 1} / {totalPages}
              </span>
              <button disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}
                style={{ ...paginBtnS, opacity: page >= totalPages - 1 ? 0.4 : 1 }}>›</button>
            </div>
          </div>
        )}
      </div>

      {/* Admin Note Modal */}
      {modal && (
        <div style={overlayS} onClick={() => setModal(null)}>
          <div style={modalS} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 6px', fontSize: '1.1rem' }}>
              {modal.action === 'COMPLETED' ? '✅ Approve Withdrawal' : '❌ Reject Withdrawal'}
            </h3>
            <p style={{ margin: '0 0 16px', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
              Withdrawal #{modal.row.id} — {modal.row.amount?.toLocaleString()} coins for {modal.row.userName || modal.row.userId}
            </p>
            <label style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', fontWeight: 600 }}>Admin Note (optional)</label>
            <textarea
              style={{ ...textareaS }}
              rows={3}
              placeholder={modal.action === 'REJECTED' ? 'Reason for rejection…' : 'Payment reference, note…'}
              value={adminNote}
              onChange={e => setAdminNote(e.target.value)}
            />
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '16px' }}>
              <button style={cancelBtnS} onClick={() => setModal(null)}>Cancel</button>
              <button
                style={{ ...primaryBtnS, background: modal.action === 'COMPLETED' ? 'linear-gradient(135deg,#22c55e,#16a34a)' : 'linear-gradient(135deg,#ef4444,#dc2626)', opacity: updating ? 0.7 : 1 }}
                disabled={updating}
                onClick={() => updateStatus(modal.row.id, modal.action, adminNote)}
              >
                {updating ? 'Updating…' : (modal.action === 'COMPLETED' ? '✅ Confirm Approve' : '❌ Confirm Reject')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const cardS       = { background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: '12px', padding: '18px', textAlign: 'center' };
const thS         = { padding: '10px 14px', textAlign: 'left', fontSize: '0.73rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', whiteSpace: 'nowrap' };
const tdS         = { padding: '10px 14px' };
const exportBtnS  = { padding: '9px 18px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '0.88rem', fontWeight: 600, background: 'rgba(99,102,241,0.15)', color: 'var(--accent-primary)' };
const primaryBtnS = { padding: '9px 18px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '0.88rem', fontWeight: 600, color: 'white' };
const cancelBtnS  = { padding: '9px 18px', borderRadius: '8px', border: '1px solid var(--border-color)', cursor: 'pointer', fontSize: '0.88rem', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' };
const processBtnS = { padding: '5px 10px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600, background: 'rgba(6,182,212,0.15)', color: '#06b6d4' };
const approveBtnS = { padding: '5px 10px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600, background: 'rgba(34,197,94,0.15)', color: '#22c55e' };
const rejectBtnS  = { padding: '5px 10px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600, background: 'rgba(239,68,68,0.15)', color: '#ef4444' };
const paginBtnS   = { padding: '5px 12px', borderRadius: '6px', border: '1px solid var(--border-color)', cursor: 'pointer', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' };
const overlayS    = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
const modalS      = { background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: '16px', padding: '28px', width: '100%', maxWidth: '480px', boxShadow: '0 24px 64px rgba(0,0,0,0.4)' };
const textareaS   = { width: '100%', marginTop: '6px', padding: '10px 12px', background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '0.88rem', resize: 'vertical', outline: 'none', boxSizing: 'border-box' };

export default Withdrawals;
